import { queryClient } from "../services/queryClient";

/**
 * Immutable updaters for the React Query caches fed by the realtime chat
 * layer. Every transform returns a NEW object along the changed path (root,
 * pages array, page, page.data, messages/chat/chats) and never mutates the
 * cached value, so memoised consumers keyed on those references re-render.
 *
 * Cache shapes:
 *   ["getMessages", chatId] -> { pages: [{ data: { messages, chat } }, ...], pageParams }
 *   ["getChats"]            -> { data: { chats: [...] } }
 *
 * The `with*` helpers are pure and exported for unit tests; the `update*`
 * functions apply them to the shared queryClient.
 */

function hasFirstPage(oldData) {
  return Boolean(
    oldData &&
      Array.isArray(oldData.pages) &&
      oldData.pages[0] &&
      oldData.pages[0].data
  );
}

function mapFirstPage(oldData, transformPageData) {
  if (!hasFirstPage(oldData)) return oldData;
  const [firstPage, ...otherPages] = oldData.pages;
  return {
    ...oldData,
    pages: [
      { ...firstPage, data: transformPageData(firstPage.data) },
      ...otherPages,
    ],
  };
}

function mapChats(oldData, transformChats) {
  if (!oldData || !oldData.data || !Array.isArray(oldData.data.chats)) {
    return oldData;
  }
  return {
    ...oldData,
    data: { ...oldData.data, chats: transformChats(oldData.data.chats) },
  };
}

/** Patch the first page's `chat` object through `updateChat(chat) => chat`. */
export function withChatUpdated(oldData, updateChat) {
  return mapFirstPage(oldData, (pageData) => ({
    ...pageData,
    chat: updateChat(pageData.chat || {}),
  }));
}

/** Set one field on the first page's `chat` object. */
export function withChatField(oldData, field, value) {
  return withChatUpdated(oldData, (chat) => ({ ...chat, [field]: value }));
}

/** Prepend a message to the first page and mark it as the latest. */
export function withNewMessage(oldData, newMessage) {
  return mapFirstPage(oldData, (pageData) => ({
    ...pageData,
    messages: [
      newMessage,
      ...(Array.isArray(pageData.messages) ? pageData.messages : []),
    ],
    chat: { ...(pageData.chat || {}), latestMessage: newMessage },
  }));
}

/** True when the chats cache already lists `chatId`. */
export function hasChat(oldData, chatId) {
  return Boolean(
    oldData?.data?.chats?.some((chat) => chat && chat._id === chatId)
  );
}

/**
 * Merge `patch` into the chat with `chatId`; with `moveToTop` the patched
 * chat is also moved to index 0 (most recent activity first).
 */
export function withChatPatched(
  oldData,
  chatId,
  patch,
  { moveToTop = false } = {}
) {
  return mapChats(oldData, (chats) => {
    const patched = chats.map((chat) =>
      chat && chat._id === chatId ? { ...chat, ...patch } : chat
    );
    if (!moveToTop) return patched;
    const index = patched.findIndex((chat) => chat && chat._id === chatId);
    if (index <= 0) return patched;
    return [patched[index], ...patched.filter((_, i) => i !== index)];
  });
}

/* ── Cache side effects ────────────────────────────────────────────── */

export function updateMessages(newMessage) {
  queryClient.setQueryData(["getMessages", newMessage.chat], (oldData) =>
    withNewMessage(oldData, newMessage)
  );

  const chatsData = queryClient.getQueryData(["getChats"]);
  if (chatsData && !hasChat(chatsData, newMessage.chat)) {
    // A message for a chat this list has never seen: refetch instead of
    // guessing the preview shape.
    queryClient.refetchQueries({
      queryKey: ["getChats"],
      type: "active",
    });
    return;
  }

  queryClient.setQueryData(["getChats"], (oldData) =>
    withChatPatched(
      oldData,
      newMessage.chat,
      { latestMessage: newMessage },
      { moveToTop: true }
    )
  );
}

export function updateSeenMessages(newMessage, chat) {
  queryClient.setQueryData(["getMessages", chat._id], (oldData) =>
    withChatField(oldData, "latestMessage", newMessage)
  );

  queryClient.setQueryData(["getChats"], (oldData) =>
    withChatPatched(oldData, newMessage.chat, { latestMessage: newMessage })
  );
}

export function updateChatTheme(newTheme, chat) {
  queryClient.setQueryData(["getMessages", chat], (oldData) =>
    withChatField(oldData, "theme", newTheme)
  );
}

export function updateChatName(newchatName, chat) {
  queryClient.setQueryData(["getMessages", chat], (oldData) =>
    withChatField(oldData, "chatName", newchatName)
  );
  queryClient.setQueryData(["getChats"], (oldData) =>
    withChatPatched(oldData, chat, { chatName: newchatName })
  );
}
